import { formatShortcut, InputRichText, Tooltip } from '@dixa/lib-shared-habla';
import { useCallback, useContext, useRef } from 'react';
import { useUnmount } from 'react-use';

import { aiApi } from 'apis/ai';
import { copilotUtils } from 'utils/copilot';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useEditorContext } from 'utils/useEditorContext';

import { PinnedActionProps } from '../copilotPinnedActions.types';

import { UIBlockerError } from './uiBlockerError';
import { UIBlockerLoading } from './uiBlockerLoading';

export const SpellingAndGrammar = ({
  text,
  csid,
}: PinnedActionProps): JSX.Element | null => {
  const [checkSpellingAndGrammar, { isFetching }] =
    aiApi.useLazyReadAiCheckGrammarAndSpellingQuery();
  const { content } = useContext(InputRichText.Context);
  const { replace } = useEditorContext();

  const { hasCopilot } = useCopilotTrial();
  const triggerRef = useRef<ReturnType<typeof checkSpellingAndGrammar> | null>(
    null,
  );

  const { isDisabled, tooltipContent } =
    copilotUtils.spellingAndGrammar.getStatus({
      text,
      isLoading: isFetching,
    });

  const shortcut = formatShortcut([[[], 'F7']]);
  const tooltipWithShortcut = shortcut
    ? `${tooltipContent} (${shortcut})`
    : tooltipContent;

  useUnmount(() => {
    if (triggerRef.current) {
      triggerRef.current.abort();
    }
  });

  const onClickHandler = useCallback(() => {
    content.setUiBlocker(
      <UIBlockerLoading loadingText="Checking spelling and grammar" />,
    );
    const trigger = checkSpellingAndGrammar({ text, csid });
    triggerRef.current = trigger;

    trigger
      .unwrap()
      .then((data) => {
        triggerRef.current = null;
        if (data.text) {
          replace(data?.text ?? '');
        }
        content.setUiBlocker(null);
      })
      .catch(() => {
        triggerRef.current = null;
        content.setUiBlocker(
          <UIBlockerError
            onClick={() => {
              content.setUiBlocker(null);
            }}
            errorText="Failed to check spelling and grammar"
          />,
        );
      });
  }, [checkSpellingAndGrammar, content, replace, text]);

  if (!hasCopilot) {
    return null;
  }

  return (
    <Tooltip content={tooltipWithShortcut}>
      <InputRichText.ToolbarButton
        icon="grammar-spelling"
        onClick={onClickHandler}
        disabled={isDisabled}
      />
    </Tooltip>
  );
};
